import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  FileText,
  MapPin,
  Users,
  Award,
  History,
  XCircle,
  Clock,
  DollarSign,
  ArrowUpDown,
  Ship,
  Plane,
  Truck,
  Train,
  Edit,
  Play,
  Pause,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { MetricCard } from '@/components/common';
import {
  rfqApi,
  FreightRFQ as FreightRFQType,
  FreightRFQLine,
  FreightRFQInvitation,
  FreightRFQResponse,
  FreightRFQAward,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

const statusColors: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-600 border-gray-200',
  Open: 'bg-blue-100 text-blue-600 border-blue-200',
  Closed: 'bg-amber-100 text-amber-600 border-amber-200',
  Awarded: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  Cancelled: 'bg-red-100 text-red-600 border-red-200',
};

const shipmentTypeIcons: Record<string, React.ReactNode> = {
  OCEAN_FCL: <Ship className="w-4 h-4" />,
  OCEAN_LCL: <Ship className="w-4 h-4" />,
  AIR: <Plane className="w-4 h-4" />,
  FTL: <Truck className="w-4 h-4" />,
  LTL: <Truck className="w-4 h-4" />,
  RAIL: <Train className="w-4 h-4" />,
  BREAK_BULK: <Ship className="w-4 h-4" />,
  INTERMODAL: <Truck className="w-4 h-4" />,
};

export default function FreightRFQDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState<FreightRFQType | null>(null);
  const [lines, setLines] = useState<FreightRFQLine[]>([]);
  const [invitations, setInvitations] = useState<FreightRFQInvitation[]>([]);
  const [responses, setResponses] = useState<FreightRFQResponse[]>([]);
  const [awards, setAwards] = useState<FreightRFQAward[]>([]);

  useEffect(() => {
    if (id) {
      loadRFQ();
    }
  }, [id]);

  const loadRFQ = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await rfqApi.getById(id);
      setRfq(data);
      setLines(data.lines || []);
      setInvitations(data.invitations || []);
    } catch (error) {
      console.error('Failed to load RFQ:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadResponses = async () => {
    if (!id) return;
    try {
      const data = await rfqApi.getResponses(id);
      setResponses(data);
    } catch (error) {
      console.error('Failed to load responses:', error);
    }
  };

  const loadAwards = async () => {
    if (!id) return;
    try {
      const data = await rfqApi.getAwards(id);
      setAwards(data);
    } catch (error) {
      console.error('Failed to load awards:', error);
    }
  };

  const handleStatusAction = async (action: 'send' | 'close' | 'cancel') => {
    if (!id) return;
    try {
      switch (action) {
        case 'send':
          await rfqApi.send(id);
          toast.success('RFQ opened and sent to carriers');
          break;
        case 'close':
          await rfqApi.close(id);
          toast.success('RFQ closed');
          break;
        case 'cancel':
          await rfqApi.cancel(id);
          toast.success('RFQ cancelled');
          break;
      }
      loadRFQ();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error(getErrorMessage(error));
    }
  };

  if (loading || !rfq) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate('/tms/rfq')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const daysToDeadline = Math.max(
    0,
    Math.ceil((new Date(rfq.responseDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
  );

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/tms/rfq')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-sm text-muted-foreground">{rfq.rfqNumber}</span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[rfq.status]}`}
              >
                {rfq.status}
              </span>
              {rfq.shipmentType && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-xs">
                  {shipmentTypeIcons[rfq.shipmentType]}
                  {rfq.shipmentType.replace('_', ' ')}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold">{rfq.title}</h1>
            {rfq.description && (
              <p className="text-muted-foreground mt-1 max-w-2xl">{rfq.description}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {rfq.status === 'Draft' && (
            <>
              <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}/edit`)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button onClick={() => handleStatusAction('send')}>
                <Play className="w-4 h-4 mr-2" />
                Open RFQ
              </Button>
            </>
          )}
          {rfq.status === 'Open' && (
            <>
              <Button variant="outline" onClick={() => handleStatusAction('close')}>
                <Pause className="w-4 h-4 mr-2" />
                Close RFQ
              </Button>
              <Button variant="destructive" onClick={() => handleStatusAction('cancel')}>
                <XCircle className="w-4 h-4 mr-2" />
                Cancel
              </Button>
            </>
          )}
          {(rfq.status === 'Open' || rfq.status === 'Closed') && (
            <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}/compare`)}>
              <ArrowUpDown className="w-4 h-4 mr-2" />
              Compare Quotes
            </Button>
          )}
          {rfq.status === 'Closed' && (
            <Button onClick={() => navigate(`/tms/rfq/${id}/awards`)}>
              <Award className="w-4 h-4 mr-2" />
              Manage Awards
            </Button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <MetricCard label="Lanes" value={lines.length} icon={<MapPin className="w-5 h-5" />} />
        <MetricCard
          label="Carriers Invited"
          value={invitations.length}
          icon={<Users className="w-5 h-5" />}
        />
        <MetricCard
          label="Quotes Received"
          value={rfq.totalQuotesReceived || 0}
          icon={<DollarSign className="w-5 h-5" />}
        />
        <MetricCard
          label="Lowest Bid"
          value={rfq.lowestBidAmount ? `$${rfq.lowestBidAmount.toLocaleString()}` : '-'}
          icon={<ArrowUpDown className="w-5 h-5" />}
        />
        <MetricCard
          label="Days to Deadline"
          value={daysToDeadline}
          icon={<Clock className="w-5 h-5" />}
        />
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid grid-cols-6 w-full max-w-4xl">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Overview
          </TabsTrigger>
          <TabsTrigger value="lanes" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Lanes
          </TabsTrigger>
          <TabsTrigger value="carriers" className="flex items-center gap-2">
            <Users className="w-4 h-4" />
            Carriers
          </TabsTrigger>
          <TabsTrigger value="quotes" className="flex items-center gap-2" onClick={loadResponses}>
            <DollarSign className="w-4 h-4" />
            Quotes
          </TabsTrigger>
          <TabsTrigger value="awards" className="flex items-center gap-2" onClick={loadAwards}>
            <Award className="w-4 h-4" />
            Awards
          </TabsTrigger>
          <TabsTrigger value="audit" className="flex items-center gap-2">
            <History className="w-4 h-4" />
            Audit
          </TabsTrigger>
        </TabsList>

        <div className="mt-6 bg-card rounded-xl border p-6">
          <TabsContent value="overview">
            <OverviewTab rfq={rfq} lines={lines} invitations={invitations} />
          </TabsContent>
          <TabsContent value="lanes">
            <LanesTab lines={lines} />
          </TabsContent>
          <TabsContent value="carriers">
            <CarriersTab invitations={invitations} />
          </TabsContent>
          <TabsContent value="quotes">
            <QuotesTab responses={responses} lines={lines} />
          </TabsContent>
          <TabsContent value="awards">
            <AwardsTab awards={awards} lines={lines} />
          </TabsContent>
          <TabsContent value="audit">
            <AuditTab rfqId={id!} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}

// Tab Components
function OverviewTab({
  rfq,
  lines,
  invitations,
}: {
  rfq: FreightRFQType;
  lines: FreightRFQLine[];
  invitations: FreightRFQInvitation[];
}) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-6">
        {/* RFQ Details */}
        <div className="space-y-4">
          <h3 className="font-semibold">RFQ Details</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Type</span>
              <p className="font-medium">{rfq.type}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Shipment Type</span>
              <p className="font-medium">{rfq.shipmentType?.replace('_', ' ') || '-'}</p>
            </div>
            {rfq.incoterm && (
              <div>
                <span className="text-muted-foreground">Incoterm</span>
                <p className="font-medium">{rfq.incoterm}</p>
              </div>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="space-y-4">
          <h3 className="font-semibold">Timeline</h3>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Response Deadline</span>
              <p className="font-medium">{new Date(rfq.responseDeadline).toLocaleDateString()}</p>
            </div>
            {rfq.awardTargetDate && (
              <div>
                <span className="text-muted-foreground">Award Target</span>
                <p className="font-medium">{new Date(rfq.awardTargetDate).toLocaleDateString()}</p>
              </div>
            )}
            {rfq.rateEffectiveDate && (
              <div>
                <span className="text-muted-foreground">Rate Effective</span>
                <p className="font-medium">
                  {new Date(rfq.rateEffectiveDate).toLocaleDateString()}
                </p>
              </div>
            )}
            {rfq.rateExpirationDate && (
              <div>
                <span className="text-muted-foreground">Rate Expiration</span>
                <p className="font-medium">
                  {new Date(rfq.rateExpirationDate).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Cargo Info */}
      {rfq.cargoDescription && (
        <div className="border-t pt-6">
          <h3 className="font-semibold mb-3">Cargo Information</h3>
          <p className="text-muted-foreground">{rfq.cargoDescription}</p>
          <div className="flex gap-4 mt-3">
            {rfq.hazmatRequired && (
              <span className="px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 rounded text-sm">
                Hazmat Required
              </span>
            )}
            {rfq.temperatureControlled && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 rounded text-sm">
                Temperature Controlled
              </span>
            )}
          </div>
        </div>
      )}

      {/* Quick Summary */}
      <div className="border-t pt-6">
        <h3 className="font-semibold mb-3">Summary</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">{lines.length}</div>
            <div className="text-sm text-muted-foreground">Lanes</div>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">{invitations.length}</div>
            <div className="text-sm text-muted-foreground">Carriers Invited</div>
          </div>
          <div className="bg-muted rounded-lg p-4">
            <div className="text-2xl font-bold">
              {invitations.filter((i) => i.status === 'Responded').length}
            </div>
            <div className="text-sm text-muted-foreground">Responses</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function LanesTab({ lines }: { lines: FreightRFQLine[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Lanes ({lines.length})</h3>
      </div>
      <div className="space-y-3">
        {lines.map((line) => (
          <div key={line.id} className="border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium text-muted-foreground">
                  #{line.lineNumber}
                </span>
                <div>
                  <div className="font-medium">
                    {line.originCity}, {line.originState}
                    <span className="mx-2 text-muted-foreground">→</span>
                    {line.destCity}, {line.destState}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {line.mode} • {line.equipmentType || 'N/A'}
                  </div>
                </div>
              </div>
              <div className="text-right">
                {line.targetRate && (
                  <div className="text-sm">Target: ${line.targetRate.toLocaleString()}</div>
                )}
                {line.award && (
                  <span className="text-xs text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 px-2 py-0.5 rounded">
                    Awarded
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {lines.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed rounded-xl">
            <MapPin className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No lanes added</p>
          </div>
        )}
      </div>
    </div>
  );
}

function CarriersTab({ invitations }: { invitations: FreightRFQInvitation[] }) {
  const statusBadges: Record<string, string> = {
    Pending: 'bg-gray-100 text-gray-600 border-gray-200',
    Sent: 'bg-blue-100 text-blue-600 border-blue-200',
    Viewed: 'bg-amber-100 text-amber-600 border-amber-200',
    Responded: 'bg-emerald-100 text-emerald-600 border-emerald-200',
    Declined: 'bg-red-100 text-red-600 border-red-200',
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Invited Carriers ({invitations.length})</h3>
        <Button variant="outline">
          <Users className="w-4 h-4 mr-2" />
          Invite More
        </Button>
      </div>
      <div className="space-y-2">
        {invitations.map((inv) => (
          <div
            key={inv.id}
            className="flex items-center justify-between p-4 border rounded-lg"
          >
            <div className="flex items-center gap-4">
              <div>
                <div className="font-medium">{inv.carrier.name}</div>
                <div className="text-sm text-muted-foreground">{inv.carrier.code}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusBadges[inv.status]}`}
              >
                {inv.status}
              </span>
              {inv.viewedAt && (
                <span className="text-xs text-muted-foreground">
                  Viewed {new Date(inv.viewedAt).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
        ))}
        {invitations.length === 0 && (
          <div className="text-center py-12 border-2 border-dashed rounded-xl">
            <Users className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <p className="text-muted-foreground">No carriers invited</p>
          </div>
        )}
      </div>
    </div>
  );
}

function QuotesTab({
  responses,
  lines,
}: {
  responses: FreightRFQResponse[];
  lines: FreightRFQLine[];
}) {
  const groupedByLine = lines.map((line) => ({
    line,
    quotes: responses.filter((r) => r.rfqLineId === line.id),
  }));

  return (
    <div className="space-y-6">
      <h3 className="font-semibold">Quotes by Lane</h3>
      {groupedByLine.map(({ line, quotes }) => (
        <div key={line.id} className="border rounded-lg overflow-hidden">
          <div className="bg-muted px-4 py-3 border-b">
            <div className="font-medium">
              Lane #{line.lineNumber}: {line.originCity} → {line.destCity}
            </div>
            <div className="text-sm text-muted-foreground">{quotes.length} quote(s) received</div>
          </div>
          {quotes.length > 0 ? (
            <div className="divide-y">
              {quotes.map((quote) => (
                <div key={quote.id} className="p-4 flex items-center justify-between">
                  <div>
                    <div className="font-medium">
                      {quote.invitation?.carrier.name || 'Unknown Carrier'}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Base: ${quote.baseRate.toLocaleString()} • Total: $
                      {quote.totalRate?.toLocaleString() || quote.baseRate.toLocaleString()}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                        quote.status === 'Submitted'
                          ? 'bg-blue-100 text-blue-600 border-blue-200'
                          : quote.status === 'Accepted'
                          ? 'bg-emerald-100 text-emerald-600 border-emerald-200'
                          : 'bg-gray-100 text-gray-600 border-gray-200'
                      }`}
                    >
                      {quote.status}
                    </span>
                    <Button variant="outline" size="sm">
                      View
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center text-muted-foreground">
              No quotes received for this lane
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AwardsTab({ awards, lines }: { awards: FreightRFQAward[]; lines: FreightRFQLine[] }) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Awards ({awards.length})</h3>
      </div>
      {awards.length > 0 ? (
        <div className="space-y-3">
          {awards.map((award) => {
            const line = lines.find((l) => l.id === award.rfqLineId);
            return (
              <div
                key={award.id}
                className="border rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">
                    Lane #{line?.lineNumber}: {line?.originCity} → {line?.destCity}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Awarded to: {award.carrier?.name} • Rate: $
                    {award.awardedBaseRate.toLocaleString()}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium border ${
                      award.status === 'Published'
                        ? 'bg-emerald-100 text-emerald-600 border-emerald-200'
                        : award.status === 'Reviewed'
                        ? 'bg-blue-100 text-blue-600 border-blue-200'
                        : 'bg-amber-100 text-amber-600 border-amber-200'
                    }`}
                  >
                    {award.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed rounded-xl">
          <Award className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">No awards yet</p>
        </div>
      )}
    </div>
  );
}

function AuditTab({ rfqId: _rfqId }: { rfqId: string }) {
  return (
    <div className="space-y-4">
      <h3 className="font-semibold">Audit Trail</h3>
      <div className="text-center py-12 border-2 border-dashed rounded-xl">
        <History className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
        <p className="text-muted-foreground">Audit log loading...</p>
      </div>
    </div>
  );
}
